/**
 * 中性化路由：**地址栏恒为 '/'，真实路径只活在 `history.state` 里**。
 *
 * 威胁模型只有一个：拿到这台已解锁手机的人。他不需要密码也不需要漏洞，
 * 在地址栏敲一个字母就看见了历史建议里的 `/chat/2`——应用锁一次都没被触发。
 * 应用锁防的是"打开应用之后"，这一条防的是"打开之前"。
 *
 * ⚠️ **默认关，opt-in。** 它换掉的是"URL 可分享 / 可收藏 / 可回溯"这个 Web 基本预期，
 * 只有伪装类、隐私类应用该付这个代价。
 *
 * 三件事必须同时成立，缺一件都是**净退步**（比不做还糟）：
 *
 * | 要保住的 | 做法 |
 * |---|---|
 * | 返回键 | 仍然 pushState，只是每条 entry 的 URL 都是中性的，靠 `state.p` 还原路径 |
 * | 通知深链 | 首帧仍按**真实 URL** 解析（`/chat/2?call=…` 必须直达），解析完立刻抹掉 |
 * | 同标签页刷新 | 路径同时记一份 sessionStorage，`history.state` 丢了还能回落 |
 *
 * ⚠️ 镜像**必须是 sessionStorage**（关标签页即失效、新标签页拿不到），与会话级门禁标志同寿命。
 * 用 localStorage 等于用"刷新能回去"把门禁拆了。
 *
 * ⚠️ 连带改动：所有"记住他本来要去哪"的逻辑（登录后回跳、门禁放行后回跳）必须改成读
 * `history.path()`。`window.location` 现在**永远**是中性 URL，不改就变成开门后送不回原页面。
 */
import { sanitizePath } from './path.js';
/** entry 上标记"这条是弹层压的" —— 见 pushHistoryEntry */
export const HISTORY_ENTRY_KEY = 'pwashipEntry';
/** 真实路径在 state 上的字段名。短是故意的：它会出现在任何人 dump 出来的 state 里。 */
export const HISTORY_PATH_KEY = 'p';
const DEFAULT_STORAGE_KEY = '__pwaship_np';
function defaultParseInitial(url) {
    return `${url.pathname}${url.search}${url.hash}`;
}
function resolveSession(session) {
    if (session !== undefined)
        return session;
    try {
        return typeof sessionStorage === 'undefined' ? null : sessionStorage;
    }
    catch {
        // 访问这个属性本身就可能抛（企业策略 / 禁用 cookie）
        return null;
    }
}
function readPath(state) {
    if (typeof state !== 'object' || state === null)
        return null;
    const p = state[HISTORY_PATH_KEY];
    return typeof p === 'string' && p ? p : null;
}
/**
 * 所有**读进来**的路径都要过这里。
 *
 * 曾经这里只有"没有前导斜杠就补一个"，于是 `//evil.test/steal` 直接放行——
 * 而那正是 `https://你的域` 后面多打一个斜杠时 `location.pathname` 的值，
 * 一条链接就能让 `path()` 交出一个站外地址。已复现，见 path.ts 的注释。
 */
function toInternalPath(input, neutralPath) {
    return sanitizePath(input, neutralPath);
}
/** 把已有 state 上除弹层标记之外的字段带走（第三方库也可能往 state 上放东西） */
function carry(state) {
    if (typeof state !== 'object' || state === null)
        return {};
    const out = { ...state };
    delete out[HISTORY_ENTRY_KEY];
    delete out[HISTORY_PATH_KEY];
    return out;
}
/**
 * 同一个 window 上活着的那一个实例。
 *
 * ⚠️ 存在的理由是 React StrictMode：它会把 `useMemo` 的 factory 跑两遍，
 * 于是造出两个实例，而**第一个永远不会被 destroy**（useMemo 丢弃旧值时不会调用清理），
 * 它的 popstate 监听就此永久留在页面上。一个地址栏只该有一个主人，
 * 所以这里让新实例接管前先把上一个拆掉。
 */
let liveInstance = null;
export function createNeutralHistory(options = {}) {
    const neutralUrl = options.neutralUrl ?? '/';
    const neutralPath = options.neutralPath ?? '/';
    const storageKey = options.storageKey ?? DEFAULT_STORAGE_KEY;
    const parseInitial = options.parseInitial ?? defaultParseInitial;
    const onError = options.onError ?? (() => { });
    const session = resolveSession(options.session);
    const listeners = new Set();
    let current = neutralPath;
    if (typeof window === 'undefined') {
        return {
            path: () => current,
            navigate: () => { },
            subscribe: () => () => { },
            destroy: () => { },
            neutralPath,
        };
    }
    const mirrorRead = () => {
        try {
            const v = session?.getItem(storageKey);
            return typeof v === 'string' && v ? v : null;
        }
        catch {
            return null;
        }
    };
    const mirrorWrite = (path) => {
        try {
            session?.setItem(storageKey, path);
        }
        catch {
            // 隐私模式下写不进去。失败偏向可用：刷新会落回中性首屏，不是把人锁在外面。
        }
    };
    const write = (path, replace, base) => {
        const state = { ...carry(base), [HISTORY_PATH_KEY]: path };
        try {
            if (replace)
                window.history.replaceState(state, '', neutralUrl);
            else
                window.history.pushState(state, '', neutralUrl);
        }
        catch (e) {
            // history API 被限流（Safari 有频率上限）时不能让路由整个崩掉
            onError('write', e);
        }
        mirrorWrite(path);
    };
    const emit = () => {
        for (const l of [...listeners]) {
            try {
                l(current);
            }
            catch (e) {
                onError('listener', e);
            }
        }
    };
    /* ── 首帧 ─────────────────────────────────────────────────────────────────
     * 顺序不能反：**先按真实 URL 解析**（通知深链 `/chat/2?call=…` 必须直达），
     * 解析完立刻 replaceState 抹掉。地址栏本来就是中性的，才轮到 state / 镜像回落。
     */
    {
        const realUrl = new URL(window.location.href);
        const parsed = toInternalPath(parseInitial(realUrl), neutralPath);
        if (parsed !== neutralPath) {
            current = parsed;
        }
        else {
            // 同标签页刷新：浏览器会保留 history.state，它是第一来源；
            // 拿不到（bfcache 未命中、会话恢复）才用 sessionStorage 镜像。
            // state 与镜像同源可写，XSS 之后就是攻击者可写——一样要净化
            const restored = readPath(window.history.state) ?? mirrorRead();
            current = restored === null ? neutralPath : toInternalPath(restored, neutralPath);
        }
        write(current, true, window.history.state);
    }
    /* ── 返回键 ────────────────────────────────────────────────────────────── */
    const onPop = (event) => {
        const p = readPath(event.state);
        // ⚠️ 拿不到 state 就落**中性首屏**，绝不落回上一次的真实路径。
        // 浏览器恢复会话、改造前遗留的老 entry 都会给你一个空 state——
        // 落错方向也要落在更安全的那边。
        const next = p === null ? neutralPath : toInternalPath(p, neutralPath);
        current = next;
        // 空 state 的老 entry 很可能带着真实 URL（`/chat/2`）被浏览器恢复出来，
        // 这时候地址栏已经泄露了。必须就地改写回中性 URL。
        const neutralHref = new URL(neutralUrl, window.location.origin).href;
        if (p === null || window.location.href !== neutralHref) {
            write(next, true, event.state);
        }
        else {
            mirrorWrite(next);
        }
        emit();
    };
    liveInstance?.destroy();
    window.addEventListener('popstate', onPop);
    const instance = {
        path: () => current,
        navigate(path, opts) {
            const next = toInternalPath(path, neutralPath);
            const replace = opts?.replace === true;
            if (next === current && !replace)
                return;
            current = next;
            write(next, replace, replace ? window.history.state : null);
            emit();
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        destroy() {
            window.removeEventListener('popstate', onPop);
            listeners.clear();
            if (liveInstance === instance)
                liveInstance = null;
        },
        neutralPath,
    };
    liveInstance = instance;
    return instance;
}
let entrySeq = 0;
/**
 * 压一条**不改变位置**的 history entry，用来接管系统返回键（弹层、看图器、筛选面板）。
 *
 * ⚠️ **这是项目里唯一允许压 history 的入口。谁都不许直接 `pushState`。**
 *
 * 原因：中性化路由靠 `state.p` 还原路径，而空 `state.p` 的安全默认是"落回中性首屏"。
 * 任何自己 `pushState({ myOverlay: 1 })` 的代码都会压出一条没有 `p` 的 entry——
 * 用户打开一个抽屉再按返回键，就被踢回首屏了：弹层关一下，页面没了。
 * 这里统一 `{ ...当前 state }` 展开，`p` 自动跟着走。
 *
 * 中性化路由没开也照样用它：URL 不变、行为一致，只是没有 `p` 可带。
 */
export function pushHistoryEntry(marker = 'overlay') {
    const inert = { isCurrent: () => false, dispose: () => { } };
    if (typeof window === 'undefined')
        return inert;
    entrySeq += 1;
    const id = `${marker}:${entrySeq}`;
    const prev = window.history.state;
    const state = {
        ...(typeof prev === 'object' && prev !== null ? prev : {}),
        [HISTORY_ENTRY_KEY]: id,
    };
    try {
        // URL 原样传回去：压 entry 是为了接管返回键，不是为了改变位置。
        // 中性化开着的时候这里是 '/'，没开的时候是真实路径——两种模式都对。
        window.history.pushState(state, '', window.location.href);
    }
    catch {
        return inert;
    }
    const isCurrent = () => {
        const s = window.history.state;
        return typeof s === 'object' && s !== null && s[HISTORY_ENTRY_KEY] === id;
    };
    return {
        isCurrent,
        dispose() {
            // 不检查就 back，会在弹层已经被返回键关掉时**又退一步**，把用户弹出上一个页面。
            if (isCurrent())
                window.history.back();
        },
    };
}
//# sourceMappingURL=nav-history.js.map